import Bugsnag from "@bugsnag/js";
import { createBrowserHistory } from "history";
import { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import NotificationsArea from "@Components/molecules/cart/NotificationsArea";
import LoginModal from "@Components/molecules/LoginModal";
import NotificationsProvider from "@Components/NotificationsStateProvider";
import Partnership from "@Components/organisms/Partnership";
import WithGlobalLoader, { useGlobalLoader } from "@Components/WithGlobalLoader";
import WithLocale from "@Components/WithLocale";
import CheerzApiProvider from "@Providers/CheerzApiProvider";
import { ScreenAction } from "@Types/login";
import { Partnership as PartnershipContent } from "@Types/partnership";
import UserContent from "@Types/user";
import { getCsrfToken, getMetaContent } from "@Utils/meta";
import { convertPartnershipFromApiToContent } from "@Utils/partnership";
import { convertToIdentifyProperties, setDataLayerTrack } from "@Utils/track";
import { convertUserFromApiToContent } from "@Utils/user";

const history = createBrowserHistory();

interface Props {
  partnershipTag: string;
}

const PartnershipPage: FunctionComponent<Props> = ({ partnershipTag }) => {
  const [action, setAction] = useState<ScreenAction>("signUp");
  const [displayLoginModal, setDisplayLoginModal] = useState<boolean>(false);
  const [partnershipData, setPartnershipData] = useState<PartnershipContent>();
  const [user, setUser] = useState<UserContent>();
  const { setLoading } = useGlobalLoader();
  const intl = useIntl();

  const handleDisplayLoginModal = (action: ScreenAction) => {
    setAction(action);
    setDisplayLoginModal(true);
  };

  useEffect(() => {
    setLoading(true);
    const cheerzApiProvider = new CheerzApiProvider(intl.locale, getCsrfToken());
    const cheerzApiV2Provider = new CheerzApiProvider(intl.locale, getCsrfToken(), true);

    cheerzApiProvider
      .getUser()
      .then((userData) => {
        setUser(convertUserFromApiToContent(userData));
        setDataLayerTrack("Global", {
          currency: userData.currency,
          environment: getMetaContent("cheerz-env") === "production" ? "production" : "test",
          locale: intl.locale,
        });
        setDataLayerTrack(
          "Identify",
          convertToIdentifyProperties(convertUserFromApiToContent(userData))
        );
        return cheerzApiV2Provider.getPartnershipPage(intl.locale, partnershipTag);
      })
      .then(({ response }) => {
        const partnershipResponse = convertPartnershipFromApiToContent(response);
        setDataLayerTrack("Page", {
          category: "Partnership",
          name: partnershipResponse.tag,
          path: `/${partnershipResponse.tag}`,
          version: "A",
        });
        setPartnershipData(partnershipResponse);
        setLoading(false);
      })
      .catch((error: Error) => {
        Bugsnag.notify(error);
      });
  }, []);

  if (user && partnershipData) {
    return (
      <>
        <WithLocale fixedLocale={partnershipData.locale} history={history}>
          <Partnership
            data={partnershipData}
            onDisplayLoginModal={() => handleDisplayLoginModal(user.isGuest ? "signUp" : "signIn")}
          />
          <LoginModal
            action={action}
            display={displayLoginModal}
            hide={() => setDisplayLoginModal(false)}
          />
          <div className="Header-notifications">
            <NotificationsArea />
          </div>
        </WithLocale>
      </>
    );
  } else return null;
};

const PartnershipPageContainer: FunctionComponent<Props> = ({ partnershipTag }) => {
  return (
    <WithGlobalLoader initialLoading={true}>
      <NotificationsProvider>
        <PartnershipPage partnershipTag={partnershipTag} />
      </NotificationsProvider>
    </WithGlobalLoader>
  );
};

export default PartnershipPageContainer;
